import { useMemo } from 'react'

import { useMapStateStore } from '../core'
import {
  type BrowseFields,
  type BrowseListView,
  defaultBrowseFields,
  Pane,
  type PaneScope,
  type SeasonFilter,
  type Session
} from '../session'
import {
  defaultPartition,
  findGroupAcross,
  groupContaining,
  type StreamPartition
} from './stream-group'
import { useGroupableStreams } from './useGroupableStreams'

/**
 * The selection rendered in a single map pane. A union over the kinds of
 * things that can be picked from the LayerPicker. New variants here should
 * carry whatever identity their data source uses as `intent` (the values that
 * round-trip through the URL); derived values like field-tag (CFF) and season
 * are resolved at call sites via hooks, not stored on the layer.
 */
export type LayerSelection =
  | AsAppliedLayer
  | FieldLayer
  | PlanningLayer
  | PlanEditorLayer
  | PlanAssetEditorLayer
  | BoundaryCreatorLayer
  | PrescriptionCreatorLayer
  | SatelliteLayer
  | SoilSampleLayer
  | SoilSurveyLayer

/**
 * An as-applied data layer — a Stream (or merged StreamGroup), with a chosen
 * Application and Layer within it. The stream's field-tag and season are
 * derived from the stream record itself (see `useStreamCff`, `useStreamSeason`
 * in the field-data feature) rather than carried here.
 */
export type AsAppliedLayer = {
  kind: 'AsApplied'
  streamId: string
  applicationId: string
  layerId: string
  mergedStreamIds: string[]
}

/**
 * A field with nothing selected over it: the map draws the field's default
 * boundary and stops there. It is what a link that names a field but no layer
 * resolves to, and where a field with no as-applied data lands — the pickers
 * still list whatever the field does have, so this is a starting point rather
 * than a dead end. Shares {@link TerrainLayer}'s precondition: the default
 * boundary is the outline it draws and zooms to.
 */
export type FieldLayer = {
  kind: 'Field'
  planningFieldId: string
}

/**
 * A plan asset shown as a top-level map layer (Boundary, Prescription, or
 * GuidancePlan). `planningFieldId` is planning's own UUID for the field
 * grouping; it is not the same identifier as a Stream's (client, farm, field)
 * tag — see `usePlanningFieldCff` to resolve a planning field to its CFF tag
 * for cross-variant relatedness queries.
 *
 * `planId` is the plan context: a string id when the asset is assigned to a
 * named plan, `null` when it belongs to the UnassignedPlanGroup pseudo-plan.
 * `AssetIdentity` is a tag-based discriminated union on `assetType` so the
 * GuidancePlan-no-assetId case is enforced structurally. Together the four
 * spec cases compose:
 *
 *   - `{ planId: 'X', assetType: 'Boundary' | 'Prescription', assetId: 'Y' }`
 *     — asset assigned to a named plan
 *   - `{ planId: 'X', assetType: 'GuidancePlan' }`
 *     — the GuidancePlan composite for plan X (no assetId; identified by the
 *       (planningFieldId, planId) pair)
 *   - `{ planId: null, assetType: 'Boundary' | 'Prescription', assetId: 'Y' }`
 *     — asset in the UnassignedPlanGroup pseudo-plan
 *   - `{ planId: null, assetType: 'GuidancePlan' }`
 *     — the unassigned-guidance composite for the field
 */
export type PlanningLayer = {
  kind: 'Planning'
  planningFieldId: string
  /** The plan id when assigned, `null` for the UnassignedPlanGroup. */
  planId: string | null
} & AssetIdentity

/** Which kind of plan asset; GuidancePlan composites have no assetId. */
export type AssetIdentity =
  | { assetType: 'Boundary' | 'Prescription'; assetId: string }
  | { assetType: 'GuidancePlan' }

/**
 * The plan editor occupying the main pane: creating a new plan
 * (`planId === null`) or editing an existing one. Not a renderable data
 * source — while this selection is active the map shows only base imagery
 * plus the draw tool's working layer. Main-pane-only by design: the
 * comparison pane never holds this variant, so the right map stays available
 * as a read-only reference during editing.
 */
export type PlanEditorLayer = {
  kind: 'PlanEditor'
  planningFieldId: string
  /** The plan being edited, or `null` when creating a new plan. */
  planId: string | null
}

/**
 * Editing a single existing plan asset (Boundary, Prescription, or GuidancePlan)
 * directly from the PlanInspector. Like PlanEditorLayer, no data source is rendered
 * — the draw tool takes over the map while editing. Main-pane-only.
 */
export type PlanAssetEditorLayer = {
  kind: 'PlanAssetEditor'
  planningFieldId: string
  planId: string | null
} & AssetIdentity

export type BoundaryCreatorLayer = {
  kind: 'BoundaryCreator'
  planningFieldId: string
}

export type PrescriptionCreatorLayer = {
  kind: 'PrescriptionCreator'
  planningFieldId: string
}

/** Which satellite product is rendered. Values double as i18n keys. */
export enum SatelliteImagery {
  /** Sentinel-2 EVI, surfaced to users as biomass. */
  Biomass = 'biomass',
  /** Sentinel-2's visible-light composite, shown as captured. */
  TrueColor = 'trueColor'
}

/**
 * Satellite imagery over one field. `captureDate` names a capture, or is `null`
 * for the most recent available — a fresh selection and a URL carrying no date
 * both mean "latest", so which capture that is resolves at render time rather
 * than being baked into the selection.
 */
export type SatelliteLayer = {
  kind: 'Satellite'
  planningFieldId: string
  imagery: SatelliteImagery
  captureDate: string | null
}

/**
 * One measurement from one soil sampling event over a field. `measurement` is
 * the analyte key as the sampling event reports it (`cec`, `pH`, `cCalcium`, …)
 * rather than a closed enum: labs report different panels, so the set of
 * available measurements is data, not code.
 */
export type SoilSampleLayer = {
  kind: 'SoilSample'
  planningFieldId: string
  samplingEventId: string
  measurement: string
}

/** Which USDA soil survey attribute colors the map units. */
export enum SoilSurveyAttribute {
  SoilType = 'soilType',
  Nccpi = 'nccpi',
  DrainageClass = 'drainageClass'
}

export type SoilSurveyLayer = {
  kind: 'SoilSurvey'
  planningFieldId: string
  attribute: SoilSurveyAttribute
}

/**
 * The map's Terrain sources — field-scoped imagery tied to neither a stream nor
 * a plan. They share a precondition: only a field with a default boundary has
 * the outline these layers are clipped and zoomed to, so a field without one
 * offers no Terrain layers at all. The union exists to state that shared
 * handling once instead of at each variant.
 */
export type TerrainLayer = SatelliteLayer | SoilSampleLayer | SoilSurveyLayer

export const isTerrainLayer = (layer: LayerSelection): layer is TerrainLayer =>
  layer.kind === 'Satellite' || layer.kind === 'SoilSample' || layer.kind === 'SoilSurvey'

/**
 * The editors, which draw nothing and are main-pane-only. Grouped here for
 * the same reason as {@link TerrainLayer}: the handling they share is stated
 * once rather than at each place that has to exclude them.
 */
export type EditorLayer =
  | PlanEditorLayer
  | PlanAssetEditorLayer
  | BoundaryCreatorLayer
  | PrescriptionCreatorLayer

export const isEditorLayer = (layer: LayerSelection): layer is EditorLayer =>
  layer.kind === 'PlanEditor' ||
  layer.kind === 'PlanAssetEditor' ||
  layer.kind === 'BoundaryCreator' ||
  layer.kind === 'PrescriptionCreator'

/**
 * The planning field a selection is scoped to, or `null` for as-applied layers
 * — those are stream-scoped, and their field comes from the stream record.
 */
export const planningFieldIdOf = (layer: LayerSelection): string | null =>
  layer.kind === 'AsApplied' ? null : layer.planningFieldId

// ---------------------------------------------------------------------------
// Helper builders
// ---------------------------------------------------------------------------

export const fieldLayer = (planningFieldId: string): FieldLayer => ({
  kind: 'Field',
  planningFieldId
})

export const asAppliedLayer = (args: {
  streamId: string
  applicationId: string
  layerId: string
  mergedStreamIds?: string[]
}): AsAppliedLayer => ({
  kind: 'AsApplied',
  mergedStreamIds: [],
  ...args
})

export const planningLayer = {
  boundary: (planningFieldId: string, planId: string | null, assetId: string): PlanningLayer => ({
    kind: 'Planning',
    planningFieldId,
    planId,
    assetType: 'Boundary',
    assetId
  }),
  prescription: (
    planningFieldId: string,
    planId: string | null,
    assetId: string
  ): PlanningLayer => ({
    kind: 'Planning',
    planningFieldId,
    planId,
    assetType: 'Prescription',
    assetId
  }),
  guidancePlan: (planningFieldId: string, planId: string | null): PlanningLayer => ({
    kind: 'Planning',
    planningFieldId,
    planId,
    assetType: 'GuidancePlan'
  })
}

export const planEditorLayer = (
  planningFieldId: string,
  planId: string | null
): PlanEditorLayer => ({
  kind: 'PlanEditor',
  planningFieldId,
  planId
})

export const planAssetEditorLayer = (
  planningFieldId: string,
  planId: string | null,
  identity: AssetIdentity
): PlanAssetEditorLayer => ({
  kind: 'PlanAssetEditor',
  planningFieldId,
  planId,
  ...identity
})

export const boundaryCreatorLayer = (planningFieldId: string): BoundaryCreatorLayer => ({
  kind: 'BoundaryCreator',
  planningFieldId
})

export const prescriptionCreatorLayer = (planningFieldId: string): PrescriptionCreatorLayer => ({
  kind: 'PrescriptionCreator',
  planningFieldId
})

export const satelliteLayer = ({
  planningFieldId,
  imagery,
  captureDate = null
}: {
  planningFieldId: string
  imagery: SatelliteImagery
  captureDate?: string | null
}): SatelliteLayer => ({ kind: 'Satellite', planningFieldId, imagery, captureDate })

export const soilSampleLayer = (args: {
  planningFieldId: string
  samplingEventId: string
  measurement: string
}): SoilSampleLayer => ({ kind: 'SoilSample', ...args })

export const soilSurveyLayer = (args: {
  planningFieldId: string
  attribute: SoilSurveyAttribute
}): SoilSurveyLayer => ({ kind: 'SoilSurvey', ...args })

// ---------------------------------------------------------------------------
// Identity equality (for reset semantics on layer change)
// ---------------------------------------------------------------------------

/**
 * True when two layers have the same identity — same stream for AsApplied,
 * same (planningFieldId, planId, assetType, assetId) for Planning, same field
 * for Terrain, Field and the boundary creator. Used by `setMainMapLayer` to
 * decide whether stream-keyed state (record, filters, passEdit) must reset.
 */
export const layersHaveSameIdentity = (a: LayerSelection, b: LayerSelection): boolean => {
  if (a.kind !== b.kind) return false
  // For Terrain the field alone is the identity: imagery, capture date and
  // measurement are choices *within* a field, the way application and layer are
  // within a stream. Nothing that resets is scoped narrower than the field.
  if (isTerrainLayer(a) && isTerrainLayer(b)) {
    return a.planningFieldId === b.planningFieldId
  }
  if (a.kind === 'AsApplied' && b.kind === 'AsApplied') {
    return a.streamId === b.streamId
  }
  if (
    (a.kind === 'Field' && b.kind === 'Field') ||
    (a.kind === 'BoundaryCreator' && b.kind === 'BoundaryCreator') ||
    (a.kind === 'PrescriptionCreator' && b.kind === 'PrescriptionCreator')
  ) {
    return a.planningFieldId === b.planningFieldId
  }
  if (a.kind === 'Planning' && b.kind === 'Planning') {
    if (
      a.planningFieldId !== b.planningFieldId ||
      a.planId !== b.planId ||
      a.assetType !== b.assetType
    ) {
      return false
    }
    const aAssetId = 'assetId' in a ? a.assetId : null
    const bAssetId = 'assetId' in b ? b.assetId : null
    return aAssetId === bAssetId
  }
  if (a.kind === 'PlanEditor' && b.kind === 'PlanEditor') {
    return a.planningFieldId === b.planningFieldId && a.planId === b.planId
  }
  if (a.kind === 'PlanAssetEditor' && b.kind === 'PlanAssetEditor') {
    if (
      a.planningFieldId !== b.planningFieldId ||
      a.planId !== b.planId ||
      a.assetType !== b.assetType
    ) {
      return false
    }
    const aAssetId = 'assetId' in a ? a.assetId : null
    const bAssetId = 'assetId' in b ? b.assetId : null
    return aAssetId === bAssetId
  }
  return false
}

// ---------------------------------------------------------------------------
// Pane access hooks
// ---------------------------------------------------------------------------

/**
 * The selection in the requested pane, or null when in BrowseFields or when
 * the comparison pane has no layer selected. `isComparison` controls which
 * pane — kept as a parameter (not split into separate hooks) so callers can
 * pick a pane dynamically from a prop.
 *
 * For AsApplied layers, the `mergedStreamIds` field is derived, not read from
 * the stored selection. A user-edited StreamGroup partition wins: the active
 * group is the one containing the selected stream, and the merge is that group
 * minus the selected stream. With no custom partition, the stream's group in
 * the default partition provides the merge.
 */
export const useMapState = (isComparison = false): LayerSelection | null => {
  const session = useMapStateStore((s) => s.session)
  const streamGroups = useMapStateStore((s) =>
    isComparison ? s.streamGroups.comparison : s.streamGroups.main
  )

  const base =
    session.kind !== 'InField'
      ? null
      : isComparison
        ? (session.comparisonMap?.selection ?? session.mainMap)
        : session.mainMap
  const groupable = useGroupableStreams(base?.kind === 'AsApplied' ? base.streamId : null)
  const defaultGroup = useMemo(
    () =>
      base?.kind === 'AsApplied'
        ? groupContaining(defaultPartition(groupable.streams), base.streamId)
        : null,
    [base, groupable.streams]
  )

  return useMemo(() => {
    if (base === null) return null
    if (base.kind !== 'AsApplied') return base

    // A custom partition wins: render the whole active group (its members minus
    // the selected stream). Robust even if the selection isn't the group's
    // primary — the union [streamId, ...mergedStreamIds] is the full group.
    const customGroup = findGroupAcross(streamGroups, base.streamId)
    if (customGroup !== null) {
      return {
        ...base,
        mergedStreamIds: customGroup.streamIds.filter((id) => id !== base.streamId)
      }
    }

    // A selection that already carries its group's members (e.g. restored from the
    // URL) is authoritative until a custom partition is built for the pane.
    if (base.mergedStreamIds.length > 0) return base

    return {
      ...base,
      mergedStreamIds: defaultGroup?.streamIds.filter((id) => id !== base.streamId) ?? []
    }
  }, [base, defaultGroup, streamGroups])
}

export class NoLayerSelectedError extends Error {
  constructor() {
    super('No layer is selected on this pane')
    this.name = 'NoLayerSelectedError'
  }
}

export class NotAsAppliedError extends Error {
  constructor(kind: LayerSelection['kind']) {
    super(`Expected AsApplied layer, got ${kind}`)
    this.name = 'NotAsAppliedError'
  }
}

/** Pane's selection or throw if absent. Use under a guard that's already proved one exists. */
export const useMapStateOrThrow = (isComparison = false): LayerSelection => {
  const layer = useMapState(isComparison)
  if (layer === null) throw new NoLayerSelectedError()
  return layer
}

/**
 * Pane's selection narrowed to AsApplied, or throw. Direct replacement for
 * the AsApplied-only consumers that previously used `useAsAppliedLayerOrThrow`.
 * Throws if the pane is empty *or* if its selection is a Planning layer.
 */
export const useAsAppliedLayerOrThrow = (isComparison = false): AsAppliedLayer => {
  const layer = useMapState(isComparison)
  if (layer === null) throw new NoLayerSelectedError()
  if (layer.kind !== 'AsApplied') throw new NotAsAppliedError(layer.kind)
  return layer
}

/**
 * Pane's selection narrowed to AsApplied, or null when the pane is empty or
 * holds another kind. For code paths that must render under any selection
 * (e.g. the plan editor) and degrade gracefully instead of throwing.
 */
export const useAsAppliedLayerOrNull = (isComparison = false): AsAppliedLayer | null => {
  const layer = useMapState(isComparison)
  return layer?.kind === 'AsApplied' ? layer : null
}

/** Comparison pane raw state (selection + disabled flag), or null when split off. */
export const useComparisonMap = (): { selection: LayerSelection; disabled: boolean } | null =>
  useMapStateStore((s) => (s.session.kind === 'InField' ? (s.session.comparisonMap ?? null) : null))

/** True when split-map is enabled and visible (comparison present and not hidden). */
export const useIsSplitActive = (): boolean =>
  useMapStateStore(
    (s) => s.session.kind === 'InField' && s.session.comparisonMap?.disabled === false
  )

/**
 * The pane's crop-cycle scope, or null when the pane's selection carries its
 * own — a pass names the crop cycle it belongs to.
 */
export const usePaneScope = (pane: Pane): PaneScope | null =>
  useMapStateStore((s) => s.paneScopes[pane])

// ---------------------------------------------------------------------------
// InField setters
// ---------------------------------------------------------------------------

/** Strip the comparison pane entirely. */
export const removeComparisonMap = () => {
  useMapStateStore.setState((prev) => {
    if (prev.session.kind !== 'InField' || prev.session.comparisonMap === null) return prev
    return { session: { ...prev.session, comparisonMap: null } }
  })
}

/** A pane's custom partition for a task key, or undefined when none has been set. */
export const useCustomStreamGroups = (pane: Pane, key: string): StreamPartition | undefined =>
  useMapStateStore((s) => s.streamGroups[pane][key])

// ---------------------------------------------------------------------------
// BrowseFields setters and selectors
// ---------------------------------------------------------------------------

const updateBrowseFields = (patch: Partial<Omit<BrowseFields, 'kind'>>) => {
  useMapStateStore.setState((prev) => {
    if (prev.session.kind !== 'BrowseFields') return prev
    return { session: { ...prev.session, ...patch } }
  })
}

export const setBrowseSeasonFilter = (seasonFilter: SeasonFilter | null) =>
  updateBrowseFields({ seasonFilter })

export const setBrowseListView = (listView: BrowseListView) => updateBrowseFields({ listView })

/** Read the active BrowseFields state, or the defaults if currently InField. */
export const useBrowseFields = (): BrowseFields =>
  useMapStateStore((s) => (s.session.kind === 'BrowseFields' ? s.session : defaultBrowseFields))

/** True when in BrowseFields. */
export const useIsBrowsing = (): boolean =>
  useMapStateStore((s) => s.session.kind === 'BrowseFields')

/** Active session (BrowseFields or InField). */
export const useSession = (): Session => useMapStateStore((s) => s.session)
